import React, { useState, useEffect } from 'react';
import CreateInvoice from './CreateInvoice';
import * as api from '../../../../services/apis';
import { getCreateInvoiceInitialValues } from './formConfig/initialValues';
import { showToast } from '../../../../helpers/showToast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  convertFormDataToApi,
  convertApiDataToForm,
} from '../../../../helpers/invoiceHelpers';
import { scrollToTop } from '../../../../helpers/common';
import { useAuthentication } from '../../../../hooks/useAuthentication';

const CreateInvoiceContainer = ({ type, taxCountry, ...props }) => {
  const [invoiceReferenceNumber, setInvoiceReferenceNumber] = useState('');
  const [category, setCategory] = useState('standard');
  const [isInvoice, setIsInvoice] = useState(type);
  const [initialForm, setInitialForm] = useState(
    getCreateInvoiceInitialValues({
      user: {},
      invoiceReferenceNumber,
      isInvoice,
      category,
      language: 'en',
    }),
  );

  const { t, i18n } = useTranslation();
  const history = useNavigate();
  const { id: editInvoiceId } = useParams();
  const isEditMode = !!editInvoiceId;
  const { userInfo } = useAuthentication();

  const [previousInvoices, setPreviousInvoices] = useState([]);
  const [invoicesCount, setInvoicesCount] = useState(0);
  const [invoicesLimit, setInvoicesLimit] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userInfo) {
      if (isEditMode) {
        setLoading(true);
        api
          .getInvoiceById(editInvoiceId, userInfo.tax_number)
          .then((resp) => {
            const invoiceData = resp.data;
            console.log(invoiceData, 'invoiceDatainvoiceData');
            const formValues = convertApiDataToForm(invoiceData);
            if (formValues) {
              setCategory(formValues.documentTransactionType);
              setIsInvoice(
                formValues?.invoiceType?.includes('NOTE') ? 'note' : 'invoice',
              );
              setInvoiceReferenceNumber(
                formValues.invoiceReferenceNumber || '',
              );
              setInitialForm(
                getCreateInvoiceInitialValues({
                  user: userInfo,
                  invoiceReferenceNumber,
                  type: isInvoice,
                  category,
                  language: i18n.language,
                  ...formValues,
                }),
              );
            }
          })
          .catch(() => {
            showToast({
              data: {
                variant: 'error',
                text: t('createInvoice.failed-to-load-draft'),
              },
            });
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setInitialForm(
          getCreateInvoiceInitialValues({
            user: userInfo,
            invoiceReferenceNumber,
            type: isInvoice,
            category,
            language: i18n.language,
          }),
        );
      }

      let invoiceType =
        category === 'standard' ? 'STANDARD_TAX' : 'SIMPLIFIED_TAX';

      api
        .getInvoiceOnboardData(userInfo.tax_number, invoiceType)
        .then((resp) => {
          setPreviousInvoices(resp.data?.previousInvoices);
          setInvoiceReferenceNumber(resp.data?.nextIRN);
          setInvoicesCount(resp.data?.invoiceCount);
          setInvoicesLimit(resp.data?.invoicesCounterLimit);
        });
    }
  }, [userInfo, editInvoiceId]);

  const handleSubmitFormClick = (values, invoiceType) => {
    if (!values.items.length) {
      showToast({
        data: {
          variant: 'error',
          text: t('create_invoice_validation.add_an_item'),
        },
      });
      return;
    }

    const data = convertFormDataToApi({
      data: values,
      type: isInvoice,
      category,
      taxCountry,
    });

    setLoading(true);

    api
      .generateInvoice(data, userInfo.tax_number)
      .then((response) => {
        setLoading(false);

        if (values.invoiceStatus === 'IN_DRAFT') {
          // Show success message for draft save
          showToast({
            data: {
              variant: 'success',
              text: t('createInvoice.draft-saved-successfully'),
            },
          });
          history(`/${i18n.language}/additional-functionalities/sme`);
        } else {
          history(
            `/${i18n.language}/additional-functionalities/sme/view-invoice/${response.data?.uniqueReferenceIdentifier}/${data.invoiceType}`,
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetInvoice = () => {
    scrollToTop();
  };

  return (
    <CreateInvoice
      taxCountry={taxCountry}
      {...props}
      type={isInvoice}
      setCategory={setCategory}
      category={category}
      handleFormSubmit={handleSubmitFormClick}
      invoiceCount={invoicesCount}
      invoiceLimit={invoicesLimit}
      formObject={initialForm}
      previousInvoices={previousInvoices}
      loading={loading}
      resetInvoice={resetInvoice}
      invoiceReferenceNumber={invoiceReferenceNumber}
    />
  );
};

export default React.memo(CreateInvoiceContainer);
